import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import DialogShell from './kit/DialogShell';
import ContactPicker from './shared/ContactPicker';
import { Button } from '@/components/ui/button';
import PickSelect from './shared/PickSelect';
import { isSpendApprovalError } from './api';
import { errorMessage } from './errors';

// "Add a person" on the deal page (ticket 191, Q15). Pick an existing contact
// and a role; the server seats them (lazy link account), puts them on the deal
// and emails them where to sign in. No typed emails: the person must be a contact.
const ROLES = [
  { value: 'realtor', label: 'Realtor' },
  { value: 'co_borrower', label: 'Co-borrower' },
];

const EMAIL_WARNINGS = {
  provider_not_configured: 'Email is not set up, so no message went out.',
  send_failed: 'The email could not be sent.',
};

function emailWarning(reason) {
  if (!reason) return 'No email was sent.';
  if (reason.startsWith('recipient_blocked')) return 'The email was not sent: this address is blocked or opted out.';
  return EMAIL_WARNINGS[reason] || 'No email was sent.';
}

export default function AddDealPersonDialog({ apiClient, dealId, open, onOpenChange, onAdded }) {
  const [contactId, setContactId] = useState(null);
  const [role, setRole] = useState('realtor');
  const [busy, setBusy] = useState(false);

  const close = (next) => {
    if (!next) { setContactId(null); setRole('realtor'); }
    onOpenChange(next);
  };

  const submit = async () => {
    if (!contactId) return;
    setBusy(true);
    try {
      const { data } = await apiClient.post(`/deals/${dealId}/participants`, { contact_id: contactId, role });
      if (data.email_sent) toast.success('Added. They were emailed how to sign in.');
      else toast.success(`Added to the deal. ${emailWarning(data.reason)}`);
      close(false);
      onAdded?.();
    } catch (e) {
      // The spend-approval 403 already got its one toast from api.js.
      if (!isSpendApprovalError(e)) toast.error(errorMessage(e, 'Could not add this person'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogShell
      open={open}
      onOpenChange={close}
      title="Add a person to this deal"
      description="They get an email about the deal and join the conversation once they sign in with that address."
      size="sm"
      data-testid="add-person-dialog"
      footer={(
        <>
          <Button type="button" variant="outline" onClick={() => close(false)} disabled={busy}>Cancel</Button>
          <Button type="button" onClick={submit} disabled={!contactId || busy} data-testid="add-person-submit">
            {busy ? <Loader2 className="animate-spin" size={14} /> : 'Add and email'}
          </Button>
        </>
      )}
    >
      <div className="space-y-4">
        <ContactPicker value={contactId} onChange={setContactId} label="Contact" allowNone={false} testId="add-person-contact" />
        <PickSelect label="Role" value={role} onChange={(v) => v && setRole(v)} options={ROLES} testId="add-person-role" labelClassName="text-xs text-slate-500" />
      </div>
    </DialogShell>
  );
}
